import type { PermissionApi } from '@data-delivery/permission-ui-api';
import {
  useBusinessGroupUsersRQ,
  useVisibleSftpUsersRQ,
} from '@data-delivery/permission-ui-data-access-state';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

export interface UseUserNavigationResult {
  context: 'business-group' | 'configuration' | 'sftp-users';
  businessGroupId?: string;
  users: PermissionApi.UserEntity[];
  orderedUsers: PermissionApi.UserEntity[];
  currentIndex: number;
  prev?: PermissionApi.UserEntity;
  next?: PermissionApi.UserEntity;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  buildDetailPath: (userId: string) => string;
  buildEditPath: (userId: string) => string;
}

export function useUserNavigation(currentUserId: string): UseUserNavigationResult {
  const location = useLocation();
  const params = useParams();
  const isBusinessGroup = location.pathname.includes('/business-groups/');
  const isSftpUsers = location.pathname.includes('/configuration/users/');
  const businessGroupId = isBusinessGroup
    ? params.businessGroupId || params.id || params.bgId || undefined
    : undefined;

  const bgUsers = useBusinessGroupUsersRQ(businessGroupId);
  const visibleUsers = useVisibleSftpUsersRQ();

  const users: PermissionApi.UserEntity[] = isBusinessGroup ? bgUsers.users : visibleUsers.users;
  const isLoading = isBusinessGroup ? bgUsers.isLoading : visibleUsers.isLoading;
  const isError = isBusinessGroup ? bgUsers.isError : visibleUsers.isError;
  const error = isBusinessGroup ? bgUsers.error : visibleUsers.error;

  const orderedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        const aLogin = (a.loginId || '').toLowerCase();
        const bLogin = (b.loginId || '').toLowerCase();
        if (aLogin < bLogin) {
          return -1;
        }

        if (aLogin > bLogin) {
          return 1;
        }

        return a.id.localeCompare(b.id);
      }),
    [users],
  );
  const currentIndex = orderedUsers.findIndex(u => u.id === currentUserId);
  const prev = currentIndex > 0 ? orderedUsers[currentIndex - 1] : undefined;
  const next =
    currentIndex >= 0 && currentIndex < orderedUsers.length - 1
      ? orderedUsers[currentIndex + 1]
      : undefined;

  const buildDetailPath = (userId: string) => {
    if (isBusinessGroup) {
      return `/business-groups/${encodeURIComponent(
        businessGroupId || '',
      )}/users/${encodeURIComponent(userId)}/detail`;
    }

    if (isSftpUsers) {
      return `/configuration/users/${encodeURIComponent(userId)}/detail`;
    }

    return `/configuration/users/${encodeURIComponent(userId)}/detail`;
  };

  const buildEditPath = (userId: string) => {
    if (isBusinessGroup) {
      return `/business-groups/${encodeURIComponent(
        businessGroupId || '',
      )}/users/${encodeURIComponent(userId)}/edit`;
    }

    if (isSftpUsers) {
      return `/configuration/users/${encodeURIComponent(userId)}/edit`;
    }

    return `/configuration/users/${encodeURIComponent(userId)}/edit`;
  };

  return {
    context: isBusinessGroup ? 'business-group' : isSftpUsers ? 'sftp-users' : 'configuration',
    businessGroupId,
    users,
    orderedUsers,
    currentIndex,
    prev,
    next,
    isLoading,
    isError,
    error,
    buildDetailPath,
    buildEditPath,
  };
}

export default useUserNavigation;
